package io.horion.auth.presentation.ui.pages.profile

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import io.horion.auth.presentation.ui.components.auth.AuthButton
import io.horion.auth.presentation.ui.components.auth.AuthTextField
import io.horion.auth.presentation.ui.theme.AuthColors
import io.horion.auth.presentation.ui.theme.AuthTheme
import io.horion.auth.presentation.ui.theme.AuthTypography
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*

data class FaqItem(
    val id: String,
    val category: String,
    val question: String,
    val answer: String
)

@Page("/faq")
@Composable
fun FaqPage() {
    val ctx = rememberPageContext()
    val colorMode = ColorMode.current
    val colors = AuthTheme.colors(colorMode)

    var searchQuery by remember { mutableStateOf("") }
    var selectedCategory by remember { mutableStateOf("Tous") }
    var expandedId by remember { mutableStateOf<String?>(null) }

    val categories = listOf("Tous", "Sécurité & 2FA", "Gestion du compte", "Connexion")

    val faqItems = remember {
        listOf(
            FaqItem(
                id = "2fa_1",
                category = "Sécurité & 2FA",
                question = "Comment fonctionne la Double Authentification (2FA) sur Orion ?",
                answer = "La 2FA ajoute une couche de sécurité supplémentaire à votre compte. Lors de votre connexion, un code de vérification temporaire vous est envoyé par SMS ou par Email selon votre canal secondaire configuré."
            ),
            FaqItem(
                id = "2fa_2",
                category = "Sécurité & 2FA",
                question = "Que faire si je ne reçois pas le code OTP par SMS ?",
                answer = "Vérifiez que votre numéro de téléphone est correct et inclut bien votre indicatif pays (ex: +237). Attendez environ 60 secondes avant de demander un nouvel envoi. Si le problème persiste, contactez le support."
            ),
            FaqItem(
                id = "2fa_3",
                category = "Sécurité & 2FA",
                question = "Puis-je désactiver la double authentification à tout moment ?",
                answer = "Oui, vous pouvez désactiver la 2FA depuis la section 'Sécurité' de votre profil en cliquant sur le switch dédié."
            ),
            FaqItem(
                id = "acc_1",
                category = "Gestion du compte",
                question = "Comment modifier mes informations personnelles (email, téléphone) ?",
                answer = "Rendez-vous dans votre espace Profil > Informations personnelles. Cliquez sur le champ à modifier puis validez les changements. Une confirmation par code OTP peut être requise."
            ),
            FaqItem(
                id = "acc_2",
                category = "Gestion du compte",
                question = "Que se passe-t-il si je perds l'accès à mon adresse Email principale ?",
                answer = "Si vous avez configuré un numéro de téléphone de secours ou activé la 2FA par SMS, vous pourrez réinitialiser votre accès. Dans le cas contraire, une vérification d'identité via le support client sera nécessaire."
            ),
            FaqItem(
                id = "auth_1",
                category = "Connexion",
                question = "J'ai oublié mon mot de passe, comment le réinitialiser ?",
                answer = "Sur la page de connexion, cliquez sur 'Mot de passe oublié'. Entrez votre email ou numéro de téléphone pour recevoir un lien ou un code de réinitialisation sécurisé."
            )
        )
    }

    // Filtrage dynamique
    val filteredFaq = faqItems.filter { item ->
        val matchesCategory = selectedCategory == "Tous" || item.category == selectedCategory
        val matchesSearch = searchQuery.isBlank() ||
                item.question.contains(searchQuery, ignoreCase = true) ||
                item.answer.contains(searchQuery, ignoreCase = true)
        matchesCategory && matchesSearch
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .minHeight(100.vh)
            .backgroundColor(colors.background),
        contentAlignment = Alignment.TopCenter
    ) {
        Column(
            modifier = Modifier
                .width(800.px)
                .maxWidth(92.percent)
                .padding(topBottom = 40.px)
                .gap(24.px)
        ) {
            // Bouton Retour
            Row(
                modifier = Modifier
                    .cursor(Cursor.Pointer)
                    .gap(8.px)
                    .onClick { ctx.router.navigateTo("/profile") },
                verticalAlignment = Alignment.CenterVertically
            ) {
                FaArrowLeft(modifier = Modifier.fontSize(14.px).color(colors.primary))
                SpanText(
                    "Retour au profil",
                    modifier = Modifier
                        .fontSize(AuthTypography.small)
                        .fontWeight(FontWeight.SemiBold)
                        .color(colors.primary)
                )
            }

            // Header FAQ
            Column(modifier = Modifier.gap(6.px)) {
                SpanText(
                    "Centre d'aide & FAQ",
                    modifier = Modifier
                        .fontSize(32.px)
                        .fontWeight(FontWeight.Bold)
                        .color(colors.onBackground)
                )
                SpanText(
                    "Trouvez rapidement des réponses à vos questions sur la sécurité et la gestion de votre compte Orion.",
                    modifier = Modifier
                        .fontSize(AuthTypography.body)
                        .color(colors.mutedText)
                )
            }

            // Barre de recherche
            AuthTextField(
                modifier = Modifier.fillMaxWidth(),
                value = searchQuery,
                onValueChange = { searchQuery = it },
                placeholder = "Rechercher une question ou un mot-clé (ex: 2FA, mot de passe...)",
                type = InputType.Text,
                leadingIcon = { FaMagnifyingGlass(modifier = Modifier.color(colors.mutedText)) }
            )

            // Filtres par catégorie
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .gap(8.px)
                    .styleModifier { property("flex-wrap", "wrap") }
            ) {
                categories.forEach { cat ->
                    val isSelected = selectedCategory == cat
                    Box(
                        modifier = Modifier
                            .padding(leftRight = 14.px, topBottom = 8.px)
                            .borderRadius(20.px)
                            .backgroundColor(if (isSelected) colors.primary else colors.inputBg)
                            .cursor(Cursor.Pointer)
                            .onClick { selectedCategory = cat }
                    ) {
                        SpanText(
                            cat,
                            modifier = Modifier
                                .fontSize(AuthTypography.small)
                                .fontWeight(if (isSelected) FontWeight.SemiBold else FontWeight.Medium)
                                .color(if (isSelected) colors.onPrimary else colors.onBackground)
                        )
                    }
                }
            }

            // Liste des accordéons FAQ
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .borderRadius(16.px)
                    .backgroundColor(colors.surface)
                    .border(1.px, LineStyle.Solid, colors.border)
            ) {
                if (filteredFaq.isEmpty()) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(40.px),
                        contentAlignment = Alignment.Center
                    ) {
                        SpanText(
                            "Aucun résultat trouvé pour votre recherche.",
                            modifier = Modifier.fontSize(AuthTypography.body).color(colors.mutedText)
                        )
                    }
                } else {
                    filteredFaq.forEachIndexed { index, item ->
                        val isExpanded = expandedId == item.id

                        FaqAccordionItem(
                            item = item,
                            isExpanded = isExpanded,
                            isLast = index == filteredFaq.lastIndex,
                            colors = colors,
                            onToggle = {
                                expandedId = if (isExpanded) null else item.id
                            }
                        )
                    }
                }
            }


        }
    }
}

@Composable
private fun FaqAccordionItem(
    item: FaqItem,
    isExpanded: Boolean,
    isLast: Boolean,
    colors: AuthColors,
    onToggle: () -> Unit
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(20.px)
                .cursor(Cursor.Pointer)
                .onClick { onToggle() },
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                item.question,
                modifier = Modifier
                    .fontSize(AuthTypography.body)
                    .fontWeight(FontWeight.SemiBold)
                    .color(colors.onBackground)
            )

            Box(modifier = Modifier.padding(left = 12.px)) {
                if (isExpanded) {
                    FaChevronUp(modifier = Modifier.fontSize(14.px).color(colors.primary))
                } else {
                    FaChevronDown(modifier = Modifier.fontSize(14.px).color(colors.mutedText))
                }
            }
        }

        if (isExpanded) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(left = 20.px, right = 20.px, bottom = 20.px)
            ) {
                SpanText(
                    item.answer,
                    modifier = Modifier
                        .fontSize(AuthTypography.small)
                        .color(colors.mutedText)
                        .styleModifier { property("line-height", "1.6") }
                )
            }
        }

        if (!isLast) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(1.px)
                    .backgroundColor(colors.border)
            )
        }
    }
}